import { titleCase } from '@westhetics/ui'
import { routes } from '@/routes'
import {
  AUDIT_TARGET_TYPES,
  type AuditAction,
  type AuditActor,
  type AuditTarget,
  type AuditTargetType,
} from './api'

/**
 * Rótulos por extenso da trilha (docs/areas/administracao.md, "Trilha"). O valor cru do verbo e
 * do tipo de alvo nunca aparece na tela: a linha lê como frase, "quem · fez o quê · com o quê".
 */

const ACTION_LABELS: Record<AuditAction, string> = {
  created: 'Criou',
  updated: 'Alterou',
  blocked: 'Bloqueou',
  unblocked: 'Desbloqueou',
  archived: 'Arquivou',
  restored: 'Restaurou',
  deleted: 'Excluiu',
  password_reset: 'Resetou a senha',
  password_changed: 'Trocou a própria senha',
  invite_resent: 'Reenviou o convite',
  role_changed: 'Trocou o papel',
  clinic_switched: 'Entrou na clínica',
  mcp_connected: 'Conectou um aplicativo',
  mcp_disconnected: 'Desconectou um aplicativo',
  send_requested: 'Pediu o envio',
  resent: 'Reenviou',
  canceled: 'Cancelou',
  signed: 'Assinou',
  published: 'Publicou',
  activated: 'Ativou',
  deactivated: 'Desativou',
  generated: 'Gerou',
  regenerated: 'Gerou de novo',
  discarded: 'Descartou',
  price_overridden: 'Alterou o valor',
  downloaded: 'Baixou',
  undone: 'Desfez',
  read: 'Consultou',
  merged: 'Unificou',
  exported: 'Exportou',
  imported: 'Importou',
  submitted: 'Respondeu',
  closed: 'Encerrou',
  revoked: 'Revogou',
  key_regenerated: 'Regenerou a chave',
  secret_revealed: 'Revelou o segredo',
  secret_rotated: 'Trocou o segredo',
  disabled: 'Desligou',
  linked: 'Vinculou',
  unlinked: 'Desvinculou',
  draft_updated: 'Alterou o rascunho',
  draft_discarded: 'Descartou o rascunho',
}

/** As ações na ordem da escolha: as mais comuns primeiro, as de acesso e de prova depois. */
export const ACTION_OPTIONS = Object.keys(ACTION_LABELS) as AuditAction[]

/** Verbo que a área ainda não registrou aqui sai neutro, nunca cru. */
export function actionLabel(action: string): string {
  return (ACTION_LABELS as Record<string, string>)[action] ?? 'Outra ação'
}

const TARGET_TYPE_LABELS: Record<AuditTargetType, string> = {
  membership: 'Usuário',
  user: 'Conta',
  clinic: 'Clínica',
  network: 'Rede',
  integration: 'App de integração',
  practitioner: 'Prestadora',
  procedure: 'Procedimento',
  patient: 'Paciente',
  questionnaire_version: 'Versão do questionário',
  clinic_question_version: 'Perguntas da clínica',
  question: 'Pergunta',
  anamnesis: 'Anamnese',
  template: 'Contrato ou termo',
  template_version: 'Versão do contrato ou termo',
  template_activation: 'Ativação do contrato ou termo',
  appointment: 'Atendimento',
  document: 'Documento',
  signature_request: 'Envio para assinatura',
  signature: 'Assinatura',
  treatment_plan: 'Plano de tratamento',
  treatment_plan_product: 'Produto do plano',
  photo: 'Foto',
  photo_session: 'Sessão de fotos',
  region: 'Região',
  manufacturer: 'Fabricante',
  catalog_product: 'Produto do catálogo',
  catalog_procedure: 'Procedimento do catálogo',
  catalog_procedure_request: 'Pedido de inclusão no catálogo',
  webhook_event: 'Evento de provedor',
  webhook_endpoint: 'Webhook',
  dashboard: 'Dashboard',
}

export function targetTypeLabel(type: string): string {
  return (TARGET_TYPE_LABELS as Record<string, string>)[type] ?? 'Outro registro'
}

/** Os tipos de alvo agrupados por área, para a escolha "o que foi alterado". */
export const TARGET_TYPE_GROUPS: { label: string; types: AuditTargetType[] }[] = [
  { label: 'Acesso', types: ['membership', 'user', 'clinic', 'network'] },
  { label: 'Integrações', types: ['integration', 'webhook_endpoint'] },
  {
    label: 'Cadastros',
    types: [
      'practitioner',
      'procedure',
      'catalog_procedure',
      'catalog_procedure_request',
      'region',
      'manufacturer',
      'catalog_product',
    ],
  },
  { label: 'Pacientes e atendimentos', types: ['patient', 'appointment'] },
  {
    label: 'Anamnese',
    types: ['anamnesis', 'clinic_question_version', 'questionnaire_version', 'question'],
  },
  {
    label: 'Documentos e assinaturas',
    types: [
      'document',
      'signature_request',
      'signature',
      'template',
      'template_version',
      'template_activation',
    ],
  },
  {
    label: 'Planos de tratamento',
    types: ['treatment_plan', 'treatment_plan_product', 'photo', 'photo_session'],
  },
  { label: 'Sistema', types: ['webhook_event', 'dashboard'] },
]

/** A escolha de alvo guardada entre navegações: um tipo, ou tudo. */
export type TargetFilter = 'all' | AuditTargetType
export const TARGET_FILTERS: readonly TargetFilter[] = ['all', ...AUDIT_TARGET_TYPES]

/**
 * Nome de pessoa em caixa de título; app e sistema como a API nomeia. A paciente que respondeu pelo
 * link não tem nome na trilha (a função SQL de hoje chama todo autor que não é pessoa de
 * "Westhetics"; a leitura por tipo vem na migration das mudanças de acesso): a tela a nomeia pelo
 * papel, como o contrato promete.
 */
export function actorName(actor: AuditActor): string {
  if (actor.type === 'patient') return 'Paciente, pelo link'
  return actor.type === 'user' && actor.id ? titleCase(actor.name) : actor.name
}

/** A linha de apoio sob o nome de quem agiu, quando não é uma pessoa logada. */
export function actorNote(actor: AuditActor): string | null {
  if (actor.type === 'integration') return 'app de integração'
  if (actor.type === 'system' || (actor.type === 'user' && !actor.id)) return 'automático'
  return null
}

/** O que a ação atingiu: o rótulo montado na leitura ou, sem ele, o tipo por extenso. */
export function targetName(target: AuditTarget): string {
  if (!target.label) return targetTypeLabel(target.type)
  return target.type === 'membership' || target.type === 'user' || target.type === 'patient'
    ? titleCase(target.label)
    : target.label
}

/** Só quem pode abrir a ação de filtro por quem agiu: pessoa ou app, nunca o sistema. */
export function canFilterByActor(actor: AuditActor): boolean {
  return actor.id !== null && (actor.type === 'user' || actor.type === 'integration')
}

export interface TargetViewer {
  isRoot: boolean
  clinicId: string | null
}

/**
 * A tela certa para o alvo, quando ela existe e a pessoa pode abri-la: telas da Westhetics só para
 * root; a clínica só se for a ativa; o que ainda está em `PlaceholderPage` não liga.
 */
export function targetRoute(target: AuditTarget, viewer: TargetViewer): string | null {
  const id = target.id
  switch (target.type) {
    case 'membership':
      return id ? routes.admin.user(id) : null
    case 'clinic':
      return id && id === viewer.clinicId ? routes.admin.clinicProfile : null
    case 'practitioner':
      return id ? routes.admin.practitioner(id) : null
    case 'procedure':
      return id ? routes.admin.procedure(id) : null
    case 'patient':
      return id ? routes.patient(id) : null
    case 'clinic_question_version':
      return routes.anamnesisQuestionnaire
    case 'template':
      return id && viewer.isRoot ? routes.admin.template(id) : null
    case 'catalog_procedure':
      return id && viewer.isRoot ? routes.admin.catalogProcedure(id) : null
    case 'questionnaire_version':
      return id && viewer.isRoot ? routes.admin.questionnaireVersion(id) : null
    case 'network':
      return id && viewer.isRoot ? routes.admin.network(id) : null
    case 'integration':
      return id ? routes.admin.integration(id) : null
    default:
      return null
  }
}

/** O rótulo do link que abre o alvo: "Abrir usuário", "Abrir Empresa". */
export function openTargetLabel(target: AuditTarget): string {
  if (target.type === 'clinic') return 'Abrir Empresa'
  if (target.type === 'clinic_question_version') return 'Abrir questionário'
  return `Abrir ${targetTypeLabel(target.type).toLocaleLowerCase('pt-BR')}`
}
